import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import { deleteBd, getTableBd, getTableByIdBd, insertTableBd } from '../../config/functions';
import { showNotification } from '../utils/NotificationFooter';
import { showConfirmDialog } from '../utils/ConfirmDialog';
import '../../css/modalTheme.css';

const ModalPlantillaOpt = ({
    rig,
    liveDataM,
    setIsLoading,
    onUpdate,
    tipo,
    show,
    handleClose,
    user,
    uid,
    theme // Tema para adaptar el modal
}) => {
    // 🎨 Usar tema recibido o valor por defecto
    const currentTheme = theme || { bgColor: '#000000', fontColor: '#ffffff', chartBorder: '#333333' };

    const [plantillas, setPlantillas] = useState([]);
    const [datosPlantillas, setDatosPlantillas] = useState([]);
    const [plantillaSeleccionada, setPlantillaSeleccionada] = useState('');
    const [isLoadingP, setIsLoadingP] = useState(false);

    // Cargar plantillas disponibles al abrir modal
    useEffect(() => {
        // Solo consultar plantillas si el modal está visible Y hay un rig válido
        if (!show || !rig || rig === 0) {
            return;
        }
        
        // Resetear estados
        setIsLoadingP(false);
        setPlantillaSeleccionada('');
        setDatosPlantillas([]);
        
        const cargarPlantillas = async () => {
            try {
                const pl = await getTableBd('getPlantillas');
                if (pl && Array.isArray(pl)) {
                    setPlantillas(pl);
                } else {
                    setPlantillas([]);
                }
            } catch (error) {
                console.error('Error cargando plantillas:', error);
                showNotification('Error cargando plantillas', 'error');
                setPlantillas([]);
            }
        };
        cargarPlantillas();
    }, [show, rig]);

    // Propagar estado de loading
    useEffect(() => {
        if (setIsLoading) {
            setIsLoading(isLoadingP);
        }
    }, [isLoadingP, setIsLoading]);

    // Cargar datos de plantilla seleccionada
    const getDatosPlantilla = async (plantillaIdn) => {
        try {
            // Para LiveData usa 'getdataplantillald'
            const dpl = await getTableByIdBd(plantillaIdn, 'getdataplantillald');
            if (dpl && Array.isArray(dpl)) {
                setDatosPlantillas(dpl);
                //console.log('📋 Datos plantilla cargados:', dpl.length, 'variables');
            } else {
                setDatosPlantillas([]);
            }
        } catch (error) {
            console.error('Error cargando datos de plantilla:', error);
            showNotification('Error cargando datos de plantilla', 'error');
            setDatosPlantillas([]);
        }
    };

    // Manejar cambio de plantilla
    const handlePlantillaChange = (e) => {
        const plantillaId = e.target.value;
        setPlantillaSeleccionada(plantillaId);
        if (plantillaId) {
            getDatosPlantilla(plantillaId);
        } else {
            setDatosPlantillas([]);
        }
    };

    // Aplicar plantilla
    const saveDefault = async () => {
        if (!plantillaSeleccionada) {
            showNotification('Seleccione una plantilla', 'warning');
            return;
        }

        if (!datosPlantillas || datosPlantillas.length === 0) {
            showNotification('La plantilla seleccionada no tiene datos', 'warning');
            return;
        }

        showConfirmDialog(
            `¿Está seguro de aplicar esta plantilla? Se eliminarán las ${liveDataM.length} variables actuales y se cargarán ${datosPlantillas.length} variables de la plantilla.`,
            async () => {
                const tldroUser = rig + ':' + uid;

                setIsLoadingP(true);
                try {
                    // 1. Eliminar variables actuales del usuario
                    await deleteBd(tldroUser, 'deletedatamatrizusuario');
                    //console.log('✅ Variables actuales eliminadas');

                    // 2. Crear plantilla completa
                    await crearPlantillaCompleta();
                    //console.log('✅ Plantilla aplicada');

                    // 3. Notificar éxito
                    showNotification('✅ Plantilla asignada correctamente. Recargando...', 'success');

                    // 4. Cerrar modal
                    handleClose();

                    // 5. Recargar datos
                    if (onUpdate) {
                        await onUpdate();
                    }

                } catch (error) {
                    console.error('Error aplicando plantilla:', error);
                    showNotification('Error al aplicar la plantilla', 'error');
                } finally {
                    setIsLoadingP(false);
                }
            }
        );
    };

    // Crear plantilla completa (insertar variables secuencialmente)
    const crearPlantillaCompleta = async () => {
        try {
            const fecha = new Date();

            // Insertar una por una de forma SECUENCIAL
            for (const dp of datosPlantillas) {
                const dataSend = {
                    nb_tldro_idn: parseInt(rig),
                    nb_var_idn: parseInt(dp.nb_var_idn),
                    nb_usurio_idn: uid,
                    nb_matr_fila: 0,
                    nb_matr_colum: parseInt(dp.nb_datos_orden),
                    vr_adt_usureg: user,
                    dt_adt_fchreg: fecha.toISOString()
                };

                // Para LiveData usa 'createvarmatriz'
                await insertTableBd('createvarmatriz', dataSend);
            }

            //console.log(`✅ ${datosPlantillas.length} variables insertadas`);
        } catch (error) {
            console.error('Error creando plantilla:', error);
            throw error;
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered dialogClassName="modal-themed-dialog">
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Modal.Title style={{ color: '#fff' }}>
                    📋 Gestionar Plantilla
                    <small style={{ display: 'block', fontSize: '14px', color: '#4ECDC4', marginTop: '4px' }}>
                        Rig: {rig} • Variables actuales: {liveDataM.length}
                    </small>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ background: '#16213e', color: '#fff' }}>
                <Form>
                    <Form.Group controlId="plantillaSelect">
                        <Form.Label style={{ color: '#4ECDC4', fontWeight: '600' }}>
                            Seleccionar Plantilla
                        </Form.Label>
                        <Form.Control 
                            as="select" 
                            value={plantillaSeleccionada}
                            onChange={handlePlantillaChange}
                            disabled={isLoadingP}
                            style={{
                                background: '#1a1a2e',
                                border: '1px solid rgba(78, 205, 196, 0.3)',
                                color: '#fff',
                                padding: '10px'
                            }}
                        >
                            <option value="">-- Seleccione una plantilla --</option>
                            {plantillas.map(p => (
                                <option
                                    key={p.nb_plantilla_idn}
                                    value={p.nb_plantilla_idn}
                                >
                                    {p.vr_plantilla_nombre}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    {datosPlantillas.length > 0 && (
                        <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(78, 205, 196, 0.1)', borderRadius: '8px', border: '1px solid rgba(78, 205, 196, 0.3)' }}>
                            <p style={{ margin: 0, color: '#4ECDC4', fontWeight: '600' }}>
                                ✅ Plantilla cargada: {datosPlantillas.length} variables
                            </p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#999' }}>
                                Al aplicar, se eliminarán las {liveDataM.length} variables actuales
                            </p>
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer style={{ background: '#16213e', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <Button 
                    variant="secondary" 
                    onClick={handleClose}
                    disabled={isLoadingP}
                    style={{ background: 'rgba(255,255,255,0.1)', border: 'none' }}
                >
                    Cerrar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={saveDefault}
                    disabled={isLoadingP || !plantillaSeleccionada || datosPlantillas.length === 0}
                    style={{ background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)', border: 'none' }}
                >
                    {isLoadingP ? 'Aplicando...' : 'Aplicar Plantilla'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalPlantillaOpt;
